import { useEffect, useMemo, useState } from 'react'
import { AgentProxyService } from '@/services/agentProxyService'
import { useToast } from '@/hooks/useToast'
import { getFilterSettings, resetFilterSettings, saveFilterSettings, type DatabaseFilterSettings } from '@/utils/databaseFilterStorage'

interface DatabaseInfo {
	name: string
}

interface Props {
	connectionId: string
	dataSourceId: string
	databaseType: 'mysql' | 'postgresql'
	isOpen: boolean
	onClose: () => void
	onApply: () => void
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const KNOWN_PG_SYSTEM_SCHEMAS = ['information_schema', 'pg_catalog', 'pg_toast', 'pg_temp', 'pg_toast_temp']

export default function DatabaseFilterDialog({ connectionId, dataSourceId, databaseType, isOpen, onClose, onApply }: Props) {
	const { showToast } = useToast()
	const [databases, setDatabases] = useState<DatabaseInfo[]>([])
	const [schemasByDatabase, setSchemasByDatabase] = useState<Record<string, string[]>>({})
	const [expandedDb, setExpandedDb] = useState<string | null>(null)

	const [hiddenDatabases, setHiddenDatabases] = useState<Set<string>>(new Set())
	const [hiddenSchemas, setHiddenSchemas] = useState<Record<string, Set<string>>>({})

	// UI state enhancements
	const [dbSearch, setDbSearch] = useState('')
	const [globalSchemaInput, setGlobalSchemaInput] = useState('')

	useEffect(() => {
		if (!isOpen) return
		const loadDatabases = async () => {
			try {
				const response = await AgentProxyService.getDatabases(connectionId)
				const mapDb = (arr: unknown[]): DatabaseInfo[] => arr
					.map((x) => {
						if (isRecord(x)) {
							const n = (x as { name?: unknown }).name
							if (typeof n === 'string') return { name: n }
							const n2 = (x as { database_name?: unknown }).database_name
							if (typeof n2 === 'string') return { name: n2 }
						}
						if (typeof x === 'string') return { name: x }
						return undefined
					})
					.filter((x): x is DatabaseInfo => !!x)

				let list: DatabaseInfo[] = []
				if (Array.isArray(response)) {
					list = mapDb(response)
				} else if (isRecord(response)) {
					const r = response
					const databasesArr = Array.isArray((r as { databases?: unknown[] }).databases)
						? (r as { databases?: unknown[] }).databases
						: Array.isArray((r as { data?: unknown[] }).data)
							? (r as { data?: unknown[] }).data
							: []
					list = mapDb(databasesArr as unknown[])
				}
				setDatabases(list)

				// Read the freshest saved settings on every open
				const currentSettings: DatabaseFilterSettings = getFilterSettings(dataSourceId, databaseType)
				setHiddenDatabases(new Set(currentSettings.hiddenDatabases))
				const hs: Record<string, Set<string>> = {}
				for (const [db, schemas] of Object.entries(currentSettings.hiddenSchemas || {})) {
					hs[db] = new Set(schemas)
				}
				setHiddenSchemas(hs)
			} catch {
				showToast('error', 'Failed to load databases')
			}
		}
		void loadDatabases()
	}, [isOpen, connectionId, dataSourceId, databaseType, showToast])

	const loadSchemas = async (databaseName: string) => {
		try {
			const response = await AgentProxyService.getSchemas(connectionId, databaseName)
			const mapSchema = (arr: unknown[]): string[] => arr
				.map((s) => {
					if (isRecord(s)) {
						const n = (s as { name?: unknown }).name
						if (typeof n === 'string') return n
						const sn = (s as { schema_name?: unknown }).schema_name
						if (typeof sn === 'string') return sn
					}
					if (typeof s === 'string') return s
					return undefined
				})
				.filter((x): x is string => typeof x === 'string')

			let list: string[] = []
			if (Array.isArray(response)) list = mapSchema(response)
			else if (isRecord(response)) {
				const r = response
				if (Array.isArray((r as { schemas?: unknown[] }).schemas)) list = mapSchema((r as { schemas?: unknown[] }).schemas as unknown[])
				else if (isRecord((r as { result?: unknown }).result)) {
					const resultObj = (r as { result?: unknown }).result
					if (isRecord(resultObj) && Array.isArray((resultObj as { schemas?: unknown[] }).schemas)) list = mapSchema((resultObj as { schemas?: unknown[] }).schemas as unknown[])
				}
			}
			setSchemasByDatabase((prev) => ({ ...prev, [databaseName]: list }))
		} catch {
			showToast('error', 'Failed to load schemas')
		}
	}

	const handleToggleDatabase = (dbName: string) => {
		setHiddenDatabases((prev) => {
			const next = new Set(prev)
			if (next.has(dbName)) next.delete(dbName)
			else next.add(dbName)
			return next
		})
	}

	const handleToggleSchema = (dbName: string, schemaName: string) => {
		setHiddenSchemas((prev) => {
			const current = prev[dbName] ?? new Set<string>()
			const nextSet = new Set(current)
			if (nextSet.has(schemaName)) nextSet.delete(schemaName)
			else nextSet.add(schemaName)
			return { ...prev, [dbName]: nextSet }
		})
	}

	const handleToggleGlobalSchema = (schemaName: string) => {
		setHiddenSchemas((prev) => {
			const current = prev['*'] ?? new Set<string>()
			const nextSet = new Set(current)
			if (nextSet.has(schemaName)) nextSet.delete(schemaName)
			else nextSet.add(schemaName)
			return { ...prev, ['*']: nextSet }
		})
	}

	const handleAddGlobalSchema = () => {
		const name = globalSchemaInput.trim()
		if (!name) return
		setHiddenSchemas((prev) => {
			const current = prev['*'] ?? new Set<string>()
			const nextSet = new Set(current)
			nextSet.add(name)
			return { ...prev, ['*']: nextSet }
		})
		setGlobalSchemaInput('')
	}

	const filteredDatabases = useMemo(() => {
		const q = dbSearch.trim().toLowerCase()
		if (!q) return databases
		return databases.filter((d) => d.name.toLowerCase().includes(q))
	}, [dbSearch, databases])

	const handleSelectAllDatabases = () => {
		setHiddenDatabases(new Set(filteredDatabases.map((d) => d.name)))
	}
	const handleClearAllDatabases = () => {
		setHiddenDatabases((prev) => {
			const next = new Set(prev)
			for (const d of filteredDatabases) next.delete(d.name)
			return next
		})
	}

	const handleApply = () => {
		const settingsToSave: DatabaseFilterSettings = {
			hiddenDatabases: Array.from(hiddenDatabases),
			hiddenSchemas: Object.fromEntries(
				Object.entries(hiddenSchemas).map(([db, set]) => [db, Array.from(set)])
			)
		}
		saveFilterSettings(dataSourceId, settingsToSave)
		onApply()
		onClose()
	}

	const handleReset = () => {
		resetFilterSettings(dataSourceId, databaseType)
		const defaults = getFilterSettings(dataSourceId, databaseType)
		setHiddenDatabases(new Set(defaults.hiddenDatabases))
		const hs: Record<string, Set<string>> = {}
		for (const [db, schemas] of Object.entries(defaults.hiddenSchemas || {})) {
			hs[db] = new Set(schemas)
		}
		setHiddenSchemas(hs)
	}

	// Aggregate known global schema candidates (defaults + any loaded schemas)
	const globalCandidates = useMemo(() => {
		if (databaseType !== 'postgresql') return [] as string[]
		const loaded = new Set<string>()
		for (const list of Object.values(schemasByDatabase)) {
			for (const s of list) loaded.add(s)
		}
		return Array.from(new Set<string>([...KNOWN_PG_SYSTEM_SCHEMAS, ...loaded])).sort()
	}, [databaseType, schemasByDatabase])

	const globalHidden = hiddenSchemas['*'] ?? new Set<string>()

	// Ensure hooks were all called before deciding not to render
	if (!isOpen) return null

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
			<div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
				<div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
					<h2 className="text-lg font-semibold text-gray-900 dark:text-white">Database & Schema Filter</h2>
					<div className="flex items-center gap-2">
						<button type="button" onClick={handleReset} className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">Reset defaults</button>
					</div>
				</div>
				<div className="p-4 overflow-y-auto flex-1 min-h-0 space-y-6">
					<p className="text-xs text-gray-500 dark:text-gray-400">Select items to exclude from the explorer. Global schema filters apply to all databases. Patterns hide exact names and any name prefixed with name_.</p>

					{/* Databases section */}
					<section>
						<div className="flex items-center justify-between mb-2">
							<h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Databases</h3>
							<div className="flex items-center gap-2">
								<input
									type="text"
									value={dbSearch}
									onChange={(e) => setDbSearch(e.target.value)}
									placeholder="Search databases..."
									className="px-2 py-1 text-xs border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:outline-none"
								/>
								<button type="button" onClick={handleSelectAllDatabases} className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">Exclude all</button>
								<button type="button" onClick={handleClearAllDatabases} className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">Include all</button>
							</div>
						</div>
						<div className="space-y-2">
							{filteredDatabases.length === 0 && (
								<div className="text-sm text-gray-500">No databases found</div>
							)}
							{filteredDatabases.map((db) => {
								const isHidden = hiddenDatabases.has(db.name)
								const schemas = schemasByDatabase[db.name] || []
								const isExpanded = expandedDb === db.name
								return (
									<div key={db.name} className="border border-gray-200 dark:border-gray-700 rounded">
										<div className="flex items-center justify-between px-3 py-2">
											<div className="flex items-center gap-3">
												<input id={`db-${db.name}`} type="checkbox" checked={isHidden} onChange={() => handleToggleDatabase(db.name)} />
												<label htmlFor={`db-${db.name}`} className="text-sm text-gray-900 dark:text-gray-100">{db.name}</label>
											</div>
											{databaseType === 'postgresql' && (
												<button
													type="button"
													className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
													onClick={async () => {
														const next = isExpanded ? null : db.name
														setExpandedDb(next)
														if (!isExpanded && !schemasByDatabase[db.name]) {
															await loadSchemas(db.name)
														}
													}}
												>
													{isExpanded ? 'Hide schemas' : 'Show schemas'}
												</button>
											)}
										</div>
										{isExpanded && databaseType === 'postgresql' && (
											<div className="px-4 pb-3">
												{schemas.length === 0 && (
													<div className="text-xs text-gray-500">No schemas</div>
												)}
												<div className="grid grid-cols-2 gap-2">
													{schemas.map((schema) => {
														const hiddenSet = hiddenSchemas[db.name] ?? new Set<string>()
														const schemaHidden = hiddenSet.has(schema)
														return (
															<label key={`${db.name}-${schema}`} className="flex items-center gap-2 text-xs text-gray-800 dark:text-gray-200">
																<input
																	type="checkbox"
																	checked={schemaHidden}
																	onChange={() => handleToggleSchema(db.name, schema)}
																/>
																<span>{schema}</span>
															</label>
														)
													})}
												</div>
											</div>
										)}
									</div>
								)
							})}
						</div>
					</section>

					{/* Global schemas section (PostgreSQL) */}
					{databaseType === 'postgresql' && (
						<section>
							<div className="flex items-center justify-between mb-2">
								<h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Global schemas</h3>
							</div>
							<div className="mb-3 grid grid-cols-2 gap-2">
								{globalCandidates.map((schema) => (
									<label key={`global-${schema}`} className="flex items-center gap-2 text-xs text-gray-800 dark:text-gray-200">
										<input type="checkbox" checked={globalHidden.has(schema)} onChange={() => handleToggleGlobalSchema(schema)} />
										<span>{schema}</span>
									</label>
								))}
							</div>
							<div className="flex items-center gap-2">
								<input
									type="text"
									value={globalSchemaInput}
									onChange={(e) => setGlobalSchemaInput(e.target.value)}
									placeholder="Add schema or prefix (e.g. pg_temp)"
									className="px-2 py-1 text-xs border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:outline-none"
									onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddGlobalSchema() } }}
								/>
								<button type="button" onClick={handleAddGlobalSchema} className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">Add</button>
							</div>
							{globalHidden.size > 0 && (
								<div className="mt-2 flex flex-wrap gap-2">
									{Array.from(globalHidden).map((s) => (
										<span key={`chip-${s}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
											{s}
											<button type="button" className="ml-1 hover:text-red-600" onClick={() => handleToggleGlobalSchema(s)}>×</button>
										</span>
									))}
								</div>
							)}
						</section>
					)}
				</div>

				<div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
					<button type="button" onClick={onClose} className="px-3 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">Close</button>
					<button type="button" onClick={handleApply} className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">Apply</button>
				</div>
			</div>
		</div>
	)
}